import {action} from 'mobx';
import s from '../../../s';
import {DefaultPCS} from '../../../rendering/render.def';
import {isNullOrWhiteSpace} from '../../../../util';
import {isEPContentClippedForSize} from '../ep-clip';
import {applyEpFitToHeight, applyEpFitToWidth, transformPtToCqh} from '../ep-content-utils';
import {loadEPFontsFromHTML} from '../ep-extended/engraved-plate-extended.store';
import {EPLineDefaultContext, EPTemplateLineInputSpec, EPTemplateOption, resolveEPLineDefault, resolveEPTemplateContent} from './ep-templates';

// Template-mode plate steps shared by EngravedPlateExtended (a product's plate templates) and
// InstPlate (the personalized-plate module of INST-PLATES): choosing a template, its default
// line values, resolving the plate HTML from the lines, the module's cut-off gate, and the input
// rows. They read and write the product store (s.ps / s.prod / DefaultPCS) exactly as the class
// component did before they moved here.

/** One input row of the active template: a titled line, with the spec/placeholder of its definition. */
export type TemplateLineRow = {number: number; title: string; placeholder?: string; spec?: EPTemplateLineInputSpec | null; optional?: boolean; plate?: number};

/** The template whose id is s.ps.EPTemplateID, from the product's mapped templates (s.prod.EPTemplates). */
export const activeTemplate = (): EPTemplateOption | undefined => (s.prod.EPTemplates || []).find(t => t.id === s.ps.EPTemplateID);

/** What the line-default tokens resolve to on this page: [InstName], [GradSchool], [Year]. */
export const templateLineDefaultContext = (): EPLineDefaultContext => ({
    instName: s.si?.InstName || '',
    gradSchool: s.si && s.si.GradSchoolID > 0 ? s.si.GSName || '' : '',
    year: new Date().getFullYear(),
});

// ep_lines[].default (INST-PLATES D5): an empty line takes its resolved default (the store's
// institution name, the grad school, the current year), so the first render of a chosen
// template shows real text instead of ghost text. The shopper edits it like typed text. It is
// the one prefill of any line kind: a dropdown line without a default starts blank (unselected
// behind its placeholder option, EPTemplateLineRow) and counts as missing until the shopper picks.
export const applyTemplateLineDefaults = action((template: EPTemplateOption | undefined, ctx: EPLineDefaultContext) => {
    if (!template) return;

    for (const lt of template.lineTitles) {
        if (!lt.defaultValue || !isNullOrWhiteSpace(s.ps[`ep${lt.number}`])) continue;

        const value = resolveEPLineDefault(lt.defaultValue, ctx);
        if (value !== '') {
            s.ps[`ep${lt.number}`] = value;
        }
    }
});

// The plate HTML updateTemplateContent wrote last, and whether the font-load listener is on.
let lastResolvedContent = '';
let refitOnFontLoad = false;

// The personalized-plate module's too-wide gate (see engraved-plates/AGENTS.md): the editor's clip rule on whatever
// the plate holds now, inst-plate products only (a blank plate measures false). Untyped-template products leave the
// flag to the Custom editor and the CAF step.
const measureInstPlateOverflow = action(() => {
    if (!s.isInstPlate) return;
    s.ui.ep_overflowing = isEPContentClippedForSize(transformPtToCqh(s.ps.EPContent || ''), s.ps.EPWidth || 2.5, s.ps.EPHeight);
});

// The fit measures each line with the canvas in the template's web font. A face that has not
// loaded yet measures in its fallback, which is narrower than Libre Baskerville, so the first
// template a page shows in a new face fitted a long line too large and it ran off the plate
// until an edit fitted it again (QA 2026-10-28). Each time fonts finish loading, the plate HTML
// is resolved again while it is still the HTML written here (a restored cart line's saved
// EPContent is never resolved again: the gate measures it in the new fonts instead).
const watchFontLoads = () => {
    if (refitOnFontLoad || typeof document === 'undefined' || !document.fonts?.addEventListener) return;
    refitOnFontLoad = true;
    document.fonts.addEventListener('loadingdone', () => {
        if (!lastResolvedContent || s.ps.EPContent !== lastResolvedContent) {
            measureInstPlateOverflow();
            return;
        }
        const before = [s.ps.EPContent, s.ps.EP2Content];
        updateTemplateContent();
        if (s.ps.EPContent !== before[0] || s.ps.EP2Content !== before[1]) {
            s.ui.doRender = true;
            s.pulse();
        }
    });
};

// Resolve both plates' HTML from the shared lines (ep1..ep10). The substitution itself
// (ghost text for blank required lines, empty text for blank optional lines, formats,
// data-ep-hide-empty collapse, auto-fit marker spans) is resolveEPTemplateContent;
// the DOM-measured auto-fit pass (applyEpFitToWidth) then bakes the fitted sizes at
// each plate's own width. Auto Fit lines start at the admin-specified default point
// size (autoFitPt) — the marker span carries it as its own inline font-size, which
// applyEpFitToWidth's inline-style walk finds first; legacy auto-fit data without a
// pt falls back to the surrounding line markup's size. A template with a height fit
// (fitHeightMarginPt) then shrinks as a whole until its text keeps that margin from
// the plate's top and bottom edges (applyEpFitToHeight).
export const updateTemplateContent = action(() => {
    const tpl = activeTemplate();
    const lineValue = (i: number) => s.ps[`ep${i}`] || '';
    const marginPt = tpl?.fitHeightMarginPt || 0;

    const plate1Width = s.ps.EPWidth || 2.5;
    const content1 = applyEpFitToHeight(applyEpFitToWidth(resolveEPTemplateContent(s.ps.EPTemplate || '', lineValue, tpl?.lineTitles, plate1Width), plate1Width), plate1Width, s.ps.EPHeight, marginPt);
    s.ps.EPContent = content1;
    measureInstPlateOverflow();
    lastResolvedContent = content1;
    watchFontLoads();
    loadEPFontsFromHTML(content1);

    if (tpl?.plate2 && s.ps.EP2Width > 0) {
        const content2 = applyEpFitToHeight(applyEpFitToWidth(resolveEPTemplateContent(tpl.plate2.content, lineValue, tpl.lineTitles, s.ps.EP2Width), s.ps.EP2Width), s.ps.EP2Width, s.ps.EP2Height, marginPt);
        s.ps.EP2Content = content2;
        loadEPFontsFromHTML(content2);
    } else {
        s.ps.EP2Content = '';
    }
});

/** The plate size on the product, the frame canvas (pcs reads s.prod) and the editor defaults. */
export const setTemplatePlateSize = action((width: number, height: number) => {
    s.ps.EPWidth = width;
    s.ps.EPHeight = height;
    s.prod.EPWidth = width;
    s.prod.EPHeight = height;
    DefaultPCS.DefaultEPWidth = width;
    DefaultPCS.DefaultEPHeight = height;
});

const clearTemplateTitles = () => {
    for (let i = 1; i <= 10; i++) {
        s.prod[`ep${i}_title`] = '';
        s.ps[`ep${i}_title`] = '';
    }
};

/**
 * Everything choosing a template sets except the line values: its id and HTML, its size,
 * the old-style overlay settings, the second plate's size/position (its body is resolved
 * by updateTemplateContent) and the line titles that drive the input rows. A restored
 * cart line uses it alone, keeping the saved lines and EPContent.
 */
export const applyTemplateFields = action((template: EPTemplateOption) => {
    s.ps.EPTemplateID = template.id;
    s.ps.EPTemplate = template.template;
    s.prod.EPTemplate = template.template;
    setTemplatePlateSize(template.width, template.height);

    // Old-style overlay settings from the template's settingsJSON.
    s.ps.ep_xpos = template.xpos || 0;
    s.ps.ep_ypos = template.ypos || 0;
    s.ps.ep_zoom = template.zoom && template.zoom > 0 ? template.zoom : 1;
    s.ps.ep_show_oldstyle = !!template.showOldstyle;

    // Second plate size/position from the template (zeroed when it has none); its
    // body is resolved from the lines by updateTemplateContent.
    s.ps.EP2Content = '';
    s.ps.EP2Width = template.plate2 ? template.plate2.width : 0;
    s.ps.EP2Height = template.plate2 ? template.plate2.height : 0;
    s.ps.ep2_xpos = template.plate2 ? template.plate2.xpos : 0;
    s.ps.ep2_ypos = template.plate2 ? template.plate2.ypos : 0;
    s.ps.ep2_zoom = template.plate2 ? template.plate2.zoom : 1;

    clearTemplateTitles();
    for (const lt of template.lineTitles) {
        s.prod[`ep${lt.number}_title`] = lt.title;
        s.ps[`ep${lt.number}_title`] = lt.title;
    }
});

/**
 * A restored cart line (share link): the template's fields, with the saved lines and the
 * saved EPContent kept as they are (no re-resolution), and the fonts that HTML names loaded.
 * A personalized plate is measured for the too-wide gate once its web fonts are in, and again
 * on each loadingdone (watchFontLoads): see engraved-plates/AGENTS.md.
 */
export const restoreTemplate = action((template: EPTemplateOption) => {
    applyTemplateFields(template);
    if (typeof document === 'undefined') return;

    loadEPFontsFromHTML(s.ps.EPContent || '');
    if (s.isInstPlate) {
        watchFontLoads();
        void (document.fonts?.ready ?? Promise.resolve()).then(() => measureInstPlateOverflow());
    }
});

/**
 * Choose a template: its fields, then the ten line values start over (a line of one
 * template is unrelated to the same line of another), lines take their resolved default
 * (a dropdown line without one starts blank behind its placeholder option), and the plate
 * HTML is resolved before the canvas re-renders.
 */
export const applyTemplate = action((template: EPTemplateOption) => {
    applyTemplateFields(template);

    for (let i = 1; i <= 10; i++) {
        s.ps[`ep${i}`] = '';
        if (s.pcs) s.pcs[`EP_${i}`] = '';
    }

    applyTemplateLineDefaults(template, templateLineDefaultContext());
    updateTemplateContent();
    s.ui.doRender = true;
    s.ui.update_pricing = true;
    s.pulse();
});

/**
 * No template: id -1, no HTML, a blank plate (EPContent '', nothing cut off), no input rows. The
 * size and the ten line values stay as they are; choosing a template starts the lines over anyway.
 */
export const clearTemplate = action(() => {
    s.ps.EPTemplateID = -1;
    s.ps.EPTemplate = '';
    s.prod.EPTemplate = '';
    s.ps.ep_xpos = 0;
    s.ps.ep_ypos = 0;
    s.ps.ep_zoom = 1;
    s.ps.ep_show_oldstyle = false;
    s.ps.EP2Content = '';
    s.ps.EP2Width = 0;
    s.ps.EP2Height = 0;
    s.ps.ep2_xpos = 0;
    s.ps.ep2_ypos = 0;
    s.ps.ep2_zoom = 1;
    clearTemplateTitles();
    s.ps.EPContent = '';
    s.ui.ep_overflowing = false;
});

/** One line typed or picked: store it, re-resolve the plate HTML, re-render and re-price. */
export const setTemplateLine = action((lineNumber: number, value: string) => {
    s.ps[`ep${lineNumber}`] = value;
    updateTemplateContent();
    s.ui.doRender = true;
    s.ui.update_pricing = true;
    s.pulse();
});

/** The input rows of the active template: every titled line (title from prod/ps ep{N}_title). */
export const templateLineRows = (): TemplateLineRow[] => {
    const tpl = activeTemplate();
    const lines: TemplateLineRow[] = [];

    for (let i = 1; i <= 10; i++) {
        const title = s.prod[`ep${i}_title`] || s.ps[`ep${i}_title`];

        if (title && title.trim() !== '') {
            const lineDef = tpl?.lineTitles.find(lt => lt.number === i);
            lines.push({number: i, title, placeholder: lineDef?.placeholder, spec: lineDef?.spec, optional: lineDef?.optional, plate: lineDef?.plate});
        }
    }

    return lines;
};
